/**
 * VehicleForm - Form component for creating/editing vehicles
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Grid,
} from "@mui/material";
import { createVehicle, updateVehicle } from "../services/vehicleService";
import { getPartners } from "../../partners/services/partnerService";

/**
 * VehicleForm Component
 */
const VehicleForm = ({ open, vehicle, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    licensePlate: "",
    make: "",
    model: "",
    year: new Date().getFullYear(),
    vehicleType: "medium",
    capacity: 0,
    ownerType: "bubakii",
    partnerId: "",
    status: "available",
  });
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingPartners, setLoadingPartners] = useState(false);

  // Load partners when ownerType is third_party
  useEffect(() => {
    if (formData.ownerType === "third_party" && partners.length === 0) {
      loadPartners();
    }
  }, [formData.ownerType]);

  // Load vehicle data when editing
  useEffect(() => {
    if (vehicle) {
      setFormData({
        licensePlate: vehicle.licensePlate || "",
        make: vehicle.make || "",
        model: vehicle.model || "",
        year: vehicle.year || new Date().getFullYear(),
        vehicleType: vehicle.vehicleType || "medium",
        capacity: vehicle.capacity || 0,
        ownerType: vehicle.ownerType || "bubakii",
        partnerId: vehicle.partnerId || "",
        status: vehicle.status || "available",
      });
    } else {
      setFormData({
        licensePlate: "",
        make: "",
        model: "",
        year: new Date().getFullYear(),
        vehicleType: "medium",
        capacity: 0,
        ownerType: "bubakii",
        partnerId: "",
        status: "available",
      });
    }
    setError(null);
  }, [vehicle, open]);

  const loadPartners = async () => {
    setLoadingPartners(true);
    try {
      const result = await getPartners({ isActive: true });
      if (result.success) {
        setPartners(result.data || []);
      }
    } catch (err) {
      console.error("Error loading partners:", err);
    } finally {
      setLoadingPartners(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === "ownerType" && value !== "third_party") {
      setFormData((prev) => ({ ...prev, partnerId: "" }));
    }
    setError(null);
  };

  const validateForm = () => {
    if (!formData.licensePlate.trim()) {
      setError("License plate is required");
      return false;
    }
    if (!formData.make.trim()) {
      setError("Make is required");
      return false;
    }
    if (!formData.model.trim()) {
      setError("Model is required");
      return false;
    }
    if (!formData.year || formData.year < 1900 || formData.year > new Date().getFullYear() + 1) {
      setError("Valid year is required");
      return false;
    }
    if (!formData.capacity || formData.capacity <= 0) {
      setError("Capacity must be greater than 0");
      return false;
    }
    if (formData.ownerType === "third_party" && !formData.partnerId) {
      setError("Partner is required for 3rd party vehicles");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const submitData = {
        ...formData,
        licensePlate: formData.licensePlate.toUpperCase().trim(),
        partnerId: formData.ownerType === "third_party" ? formData.partnerId : undefined,
      };

      const result = vehicle
        ? await updateVehicle(vehicle.id, submitData)
        : await createVehicle(submitData);

      if (result.success) {
        onSave();
      } else {
        setError(result.error || "Failed to save vehicle");
      }
    } catch (err) {
      setError(err.message || "Failed to save vehicle");
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1899 }, (_, i) => currentYear - i);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{vehicle ? "Edit Vehicle" : "Create Vehicle"}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="License Plate"
                value={formData.licensePlate}
                onChange={(e) => handleChange("licensePlate", e.target.value.toUpperCase())}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Make"
                value={formData.make}
                onChange={(e) => handleChange("make", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Model"
                value={formData.model}
                onChange={(e) => handleChange("model", e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Year</InputLabel>
                <Select
                  value={formData.year}
                  label="Year"
                  onChange={(e) => handleChange("year", e.target.value)}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  value={formData.vehicleType}
                  label="Vehicle Type"
                  onChange={(e) => handleChange("vehicleType", e.target.value)}
                >
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="large">Large</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity (kg)"
                type="number"
                value={formData.capacity}
                onChange={(e) => handleChange("capacity", parseInt(e.target.value) || 0)}
                required
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Owner Type</InputLabel>
                <Select
                  value={formData.ownerType}
                  label="Owner Type"
                  onChange={(e) => handleChange("ownerType", e.target.value)}
                >
                  <MenuItem value="bubakii">Bubakii</MenuItem>
                  <MenuItem value="third_party">3rd Party</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.ownerType === "third_party" && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Partner</InputLabel>
                  <Select
                    value={formData.partnerId}
                    label="Partner"
                    onChange={(e) => handleChange("partnerId", e.target.value)}
                    disabled={loadingPartners}
                  >
                    {partners.map((partner) => (
                      <MenuItem key={partner.id} value={partner.id}>
                        {partner.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {vehicle && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => handleChange("status", e.target.value)}
                  >
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="in_use">In Use</MenuItem>
                    <MenuItem value="maintenance">Maintenance</MenuItem>
                    <MenuItem value="unavailable">Unavailable</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? "Saving..." : vehicle ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleForm;

